import { useEffect, useRef, useState } from "react";
import classNames from "classnames";

export const UNTITLED_CAREER_PLAN_TITLE = "Untitled Career Plan";

type CareerPlanTitleProps = {
  title: string;
  onTitleChange?: (title: string) => void;
  disabled?: boolean;
};

export default function CareerPlanTitle({ title, onTitleChange, disabled = false }: CareerPlanTitleProps) {
  const displayTitle = title.trim() || UNTITLED_CAREER_PLAN_TITLE;

  if (!onTitleChange) {
    return <h1 className="text-headline-medium-bold font-serif m-0">{displayTitle}</h1>;
  }

  return (
    <EditableTitle
      title={displayTitle}
      onTitleChange={onTitleChange}
      disabled={disabled}
      className="text-headline-medium-bold font-serif"
    />
  );
}

function EditableTitle({
  title,
  onTitleChange,
  disabled,
  className
}: {
  title: string;
  onTitleChange: (newTitle: string) => void;
  disabled: boolean;
  className: string;
}) {
  const [isTitleFocused, setIsTitleFocused] = useState(false);
  const [draftTitle, setDraftTitle] = useState(title);
  const titleTextareaRef = useRef<HTMLTextAreaElement>(null);
  const titleButtonRef = useRef<HTMLButtonElement>(null);
  const titleSavingRef = useRef(false);
  const restoreTitleButtonFocus = useRef(false);

  useEffect(() => {
    setDraftTitle(title);
  }, [title]);

  useEffect(() => {
    if (isTitleFocused && titleTextareaRef.current) {
      titleSavingRef.current = false;
      const textarea = titleTextareaRef.current;
      textarea.focus();
      textarea.setSelectionRange(0, textarea.value.length);
      return;
    }
    if (!isTitleFocused && restoreTitleButtonFocus.current) {
      restoreTitleButtonFocus.current = false;
      titleButtonRef.current?.focus();
    }
  }, [isTitleFocused]);

  const saveTitle = ({ restoreFocus = false } = {}) => {
    if (titleSavingRef.current) return;
    titleSavingRef.current = true;
    restoreTitleButtonFocus.current = restoreFocus;
    setIsTitleFocused(false);
    const normalizedTitle = draftTitle.trim();
    if (normalizedTitle && normalizedTitle !== title) {
      onTitleChange(normalizedTitle);
    } else {
      setDraftTitle(title);
    }
  };

  const handleTitleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      saveTitle({ restoreFocus: true });
    } else if (e.key === "Escape") {
      e.preventDefault();
      titleSavingRef.current = true;
      restoreTitleButtonFocus.current = true;
      setDraftTitle(title);
      setIsTitleFocused(false);
    }
  };

  if (isTitleFocused) {
    return (
      <h1 className="inline-grid min-w-0 w-full m-0 font-inherit" style={{ gridTemplateColumns: "1fr" }}>
        <span
          aria-hidden
          className={classNames(
            className,
            "invisible whitespace-pre-wrap break-words overflow-hidden px-2 py-1 border-2"
          )}
          style={{ gridArea: "1 / 1" }}
        >
          {draftTitle || "\u00A0"}
          {"\u00A0"}
        </span>
        <textarea
          ref={titleTextareaRef}
          aria-label="Edit title"
          data-testid="editable-title-input"
          value={draftTitle}
          onChange={(e) => setDraftTitle(e.target.value)}
          onBlur={() => saveTitle()}
          onKeyDown={handleTitleKeyDown}
          rows={1}
          className={classNames(
            className,
            "bg-transparent outline-none border-2 border-sys-brand-primary rounded-md px-2 py-1 min-w-0 resize-none overflow-hidden"
          )}
          style={{ gridArea: "1 / 1" }}
        />
      </h1>
    );
  }

  return (
    <h1 className="m-0">
      <button
        type="button"
        ref={titleButtonRef}
        data-testid="editable-title"
        disabled={disabled}
        className={classNames(
          className,
          "appearance-none bg-transparent text-left w-full rounded-md px-2 -mx-2 border-2 border-transparent",
          disabled
            ? "cursor-not-allowed"
            : [
                "cursor-pointer",
                "hover:border-sys-brand-outline-variant hover:bg-sys-brand-surface-variant hover:bg-opacity-10",
                "hover:![text-decoration-line:none]"
              ],
          "transition-colors"
        )}
        style={{
          textDecorationLine: "underline",
          textDecorationStyle: "dotted",
          textDecorationThickness: "2px",
          textUnderlineOffset: "8px",
          textDecorationColor: "rgba(0, 0, 0, 0.25)"
        }}
        onClick={() => setIsTitleFocused(true)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setIsTitleFocused(true);
          }
        }}
      >
        {title}
      </button>
    </h1>
  );
}
